import React, { useContext, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import Button from './Buttons/Button'
import ModalContext from '../context/ModalContext'
import useResizeObserver from '../hooks/useResizeObserver'
import CircleLoader from './Loaders/CircleLoader'
import useModalContentSize from '../hooks/useModalContentSize'

/* global ymaps */

const Wrapper = styled.div.attrs(props => ({
  style: {
    width: props.width + 'px',
    minHeight: props.height + 'px',
    maxHeight: props.height + 'px',
  },
}))`
  position: relative;
  display: flex;
  flex-direction: column;
`

const MapWrapper = styled.div.attrs(props => ({
  style: {
    width: props.width + 'px',
  },
}))`
  position: relative;
  border: 1px solid var(--line-color);
  overflow: hidden;
  flex: 1;
`

const FooterWrapper = styled.div.attrs(props => ({
  style: {
    width: props.width + 'px',
  },
}))`
  display: flex;
  flex-direction: ${props => props.$isSmallScreen ? 'column' : 'row'};
  gap: 10px;
  margin-top: 15px;
  justify-content: flex-end;
  flex-shrink: 0;
`

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 36px;
  max-height: 36px;
  width: 100%;
  span {
    font-size: 12px;
    color: grey;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const ButtonsWrapper = styled.div`
  display: flex;
  column-gap: 10px;
  justify-content: flex-end;
`

const ResetButtonSvg = styled.svg`
  position: absolute;
  bottom: 30px;
  right: 10px;
  z-index: 1;
  cursor: pointer;

  &:hover {
    transform: scale(1.1);
  }

  & path {
    fill: #646464;
  }

  &:hover path {
    fill: #333333;
  }
`

export const ResetButton = ({ onClick }) => {
  return (
    <ResetButtonSvg onClick={onClick} width="26px" height="26px" viewBox="0 0 20 20">
      <g id="Page-1" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <g id="Dribbble-Light-Preview" transform="translate(-300.000000, -7839.000000)" fill="#646464">
          <g id="icons" transform="translate(56.000000, 160.000000)">
            <path d="M255,7689 C255,7689.552 254.552,7690 254,7690 C253.448,7690 253,7689.552 253,7689 C253,7688.448 253.448,7688 254,7688 C254.552,7688 255,7688.448 255,7689 M259,7690 L259.91,7690 C259.486,7692.507 257.507,7694.486 255,7694.91 L255,7694 C255,7693.448 254.552,7693 254,7693 C253.448,7693 253,7693.448 253,7694 L253,7694.91 C250.493,7694.486 248.514,7692.507 248.09,7690 L249,7690 C249.552,7690 250,7689.552 250,7689 C250,7688.448 249.552,7688 249,7688 L248.09,7688 C248.514,7685.493 250.493,7683.514 253,7683.09 L253,7684 C253,7684.552 253.448,7685 254,7685 C254.552,7685 255,7684.552 255,7684 L255,7683.09 C257.507,7683.514 259.486,7685.493 259.91,7688 L259,7688 C258.448,7688 258,7688.448 258,7689 C258,7689.552 258.448,7690 259,7690 M263,7688 L261.931,7688 C261.479,7684.383 258.617,7681.521 255,7681.069 L255,7680 C255,7679.448 254.552,7679 254,7679 C253.448,7679 253,7679.448 253,7680 L253,7681.069 C249.383,7681.521 246.521,7684.383 246.069,7688 L245,7688 C244.448,7688 244,7688.448 244,7689 C244,7689.552 244.448,7690 245,7690 L246.069,7690 C246.521,7693.617 249.383,7696.479 253,7696.931 L253,7698 C253,7698.552 253.448,7699 254,7699 C254.552,7699 255,7698.552 255,7698 L255,7696.931 C258.617,7696.479 261.479,7693.617 261.931,7690 L263,7690 C263.552,7690 264,7689.552 264,7689 C264,7688.448 263.552,7688 263,7688" id="target-[#82]">
            </path>
          </g>
        </g>
      </g>
    </ResetButtonSvg>
  )
}

export const AddressPicker = (props) => {
  const { address, setAddress, latitude, setLatitude, longitude, setLongitude } = props
  const { closeModal } = useContext(ModalContext)
  const { windowSize, isSmallScreen, modalWrapperWidth, modalWrapperHeight, modalContentWidth } = useModalContentSize()
  const mainState = useSelector(state => state.main)
  const defaultCenter = mainState.defaultMapCenter
  const defaultZoom = 10
  const mapContainer = useRef(null)
  const mapRef = useRef(null)
  const [addressState, setAddressState] = useState({
    address: address || '',
    latitude: latitude || null,
    longitude: longitude || null
  })
  const [isYmapsReady, setIsYmapsReady] = useState(false)
  const placemarkRef = useRef(null)

  const createPlacemark = (coordinates) => {
    if (mapRef.current && coordinates && coordinates.length === 2) {
      if (placemarkRef.current) {
        mapRef.current.geoObjects.remove(placemarkRef.current)
        placemarkRef.current = null
      }

      const newPlacemark = new ymaps.Placemark(coordinates, {}, {
        draggable: true,
      })

      newPlacemark.events.add('dragend', () => {
        const newCoordinates = newPlacemark.geometry.getCoordinates()

        ymaps.geocode(newCoordinates).then((res) => {
          const firstGeoObject = res.geoObjects.get(0)
          const newAddress = firstGeoObject.getAddressLine()

          setAddressState({
            address: newAddress,
            latitude: newCoordinates[0],
            longitude: newCoordinates[1]
          })
        }).catch((error) => {
          console.log('Geocoding error: ', error)
        })
      })

      placemarkRef.current = newPlacemark
      mapRef.current.geoObjects.add(newPlacemark)
    }
  }

  useEffect(() => {
    try {
      if (!isYmapsReady) return

      ymaps.ready(() => {
        let initialCenter = defaultCenter
        let initialZoom = defaultZoom

        if (addressState.latitude && addressState.longitude) {
          initialCenter = [addressState.latitude, addressState.longitude]
          initialZoom = 14
        }

        const map = new ymaps.Map(mapContainer.current, {
          center: initialCenter,
          zoom: initialZoom,
          controls: [],
        })

        mapRef.current = map

        const zoomControl = new ymaps.control.ZoomControl({
          options: {
            size: 'small',
            position: {
              top: 10,
              right: 10,
            },
          },
        })

        map.controls.add(zoomControl)

        const searchControl = new ymaps.control.SearchControl({
          options: {
            noPlacemark: true,
            placeholderContent: 'Поиск...',
          },
        })

        map.controls.add(searchControl)

        searchControl.events.add('resultselect', (e) => {
          const results = searchControl.getResultsArray()
          const selected = results[e.get('index')]
          const coordinates = selected.geometry.getCoordinates()
          const newAddress = selected.properties.get('text')

          setAddressState({
            address: newAddress,
            latitude: coordinates[0],
            longitude: coordinates[1]
          })

          createPlacemark(coordinates)
        })

        if (address && latitude && longitude) {
          createPlacemark([latitude, longitude])
        }

        map.events.add('click', (e) => {
          const coordinates = e.get('coords')

          createPlacemark(coordinates)

          ymaps.geocode(coordinates).then((res) => {
            const firstGeoObject = res.geoObjects.get(0)
            const newAddress = firstGeoObject.getAddressLine()

            setAddressState({
              address: newAddress,
              latitude: coordinates[0],
              longitude: coordinates[1]
            })
          }).catch((error) => {
            console.log('Geocoding error: ', error)
          })
        })
      })
    } catch (error) {
      console.error('Failed to load Yandex Maps API', error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isYmapsReady])

  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.container.fitToViewport()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef.current])

  const observerRef = useResizeObserver(() => {
    if (mapRef.current && mapContainer.current) {
      mapRef.current.container.fitToViewport()
    }
  }, 100)

  useEffect(() => {
    if (observerRef.current && mapContainer.current) {
      observerRef.current.observe(mapContainer.current)
    }

    return () => {
      if (observerRef.current && mapContainer.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        observerRef.current.unobserve(mapContainer.current)
      }
    }
  }, [observerRef, windowSize])

  useEffect(() => {
    if (window.ymaps) {
      ymaps.ready(() => {
        setIsYmapsReady(true)
      })
    } else {
      const ymapsCheckInterval = setInterval(() => {
        if (window.ymaps) {
          clearInterval(ymapsCheckInterval)
          ymaps.ready(() => {
            setIsYmapsReady(true)
          })
        }
      }, 500)
      return () => clearInterval(ymapsCheckInterval)
    }
  }, [])

  useEffect(() => {
    setAddressState({
      address: address || '',
      latitude: latitude || null,
      longitude: longitude || null
    })
  }, [address, latitude, longitude])

  return (
    <Wrapper isSmallScreen={isSmallScreen} height={modalWrapperHeight} width={modalWrapperWidth}>
      <MapWrapper
        ref={mapContainer}
        width={modalContentWidth}
      >
        {!isYmapsReady &&
          <CircleLoader color='var(--main-color)' />
        }
        <ResetButton onClick={(e) => {
          e.preventDefault()
          if (placemarkRef.current && placemarkRef.current.geometry) {
            const coordinates = placemarkRef.current.geometry.getCoordinates()

            if (mapRef.current && typeof mapRef.current.setCenter === 'function' && typeof mapRef.current.setZoom === 'function') {
              mapRef.current.setCenter(coordinates, 14, { checkZoomRange: true })
            } else {
              console.log('Map reference is not available or methods are undefined')
            }

          } else {
            mapRef.current.setCenter(defaultCenter)
            mapRef.current.setZoom(defaultZoom)
          }
        }}
        />
      </MapWrapper>
      <FooterWrapper $isSmallScreen={isSmallScreen} width={modalContentWidth}>
        <InfoWrapper>
          <span>{addressState.latitude && addressState.longitude ? `${addressState.latitude}, ${addressState.longitude}` : '00.0, 00.0'} </span>
          <span>{addressState.address ? addressState.address : '-'}</span>
        </InfoWrapper>
        <ButtonsWrapper>
          <Button
            style={{
              color: '#8e8e8e',
              backgroundColor: '#FFFFFF',
              border: 'solid #8e8e8e 1px',
              minWidth: '130px',
              maxWidth: '130px',
            }}
            onClick={() => closeModal()}
            waves={false}
          >
            Отмена
          </Button>
          <Button
            style={{ minWidth: '130px' }}
            onClick={(e) => {
              e.preventDefault()
              setAddress(addressState.address)
              setLatitude(addressState.latitude)
              setLongitude(addressState.longitude)
              closeModal()
            }}
          >
            Сохранить
          </Button>
        </ButtonsWrapper>
      </FooterWrapper>
    </Wrapper>
  )
}